import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  LiveTv as LiveIcon,
  People as PeopleIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useLiveQuizSessions } from '../../hooks/useLiveQuiz';

// Styled components following SongQ design system
const ManagementContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#121212',
  minHeight: '100vh',
  padding: '24px',
}));

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#1A1A1A',
  border: '1px solid #333',
  borderRadius: '12px',
  marginBottom: '24px',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#4CAF50',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(76, 175, 80, 0.1)',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#4CAF50',
  color: '#fff',
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '8px 16px',
  '&:hover': {
    backgroundColor: '#45a049',
  },
  '&.MuiButton-outlined': {
    borderColor: '#4CAF50',
    color: '#4CAF50',
    '&:hover': {
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
    },
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  backgroundColor: status === 'active' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(158, 158, 158, 0.2)',
  color: status === 'active' ? '#4CAF50' : '#9E9E9E',
  border: `1px solid ${status === 'active' ? '#4CAF50' : '#9E9E9E'}`,
  fontWeight: 600,
}));

const LiveQuizManagement = () => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [participantsDialogOpen, setParticipantsDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [difficulties, setDifficulties] = useState([]);
  const [loadingDifficulties, setLoadingDifficulties] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  
  const {
    sessions,
    loading,
    error,
    fetchSessions,
    createSession: createSessionAPI,
    updateSession: updateSessionAPI,
    deleteSession: deleteSessionAPI,
    toggleSessionStatus: toggleSessionStatusAPI
  } = useLiveQuizSessions(false); // Disable auto-refresh to prevent modal closing

  // Form state
  const [formData, setFormData] = useState({
    quiz_name: '',
    quiz_config: {
      duration: 5,
      difficulty_id: null, // Will be set to first available difficulty
      questions_per_song: 3,
      number_of_songs: 20
    },
    quiz_data: {
      questions: []
    },
    scheduled_start_time: '',
    scheduled_end_time: ''
  });

  // Fetch difficulties from API
  const fetchDifficulties = useCallback(async () => {
    try {
      setLoadingDifficulties(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/quiz/config`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch difficulties');
      }
      
      const config = await response.json();
      setDifficulties(config.difficulties || []);
      
      // Set default difficulty to first available if not set
      if (config.difficulties && config.difficulties.length > 0 && !formData.quiz_config.difficulty_id) {
        setFormData(prev => ({
          ...prev,
          quiz_config: {
            ...prev.quiz_config,
            difficulty_id: config.difficulties[0].difficulty_id
          }
        }));
      }
    } catch (err) {
      console.error('Error fetching difficulties:', err);
      enqueueSnackbar('Failed to fetch difficulties', { variant: 'error' });
    } finally {
      setLoadingDifficulties(false);
    }
  }, [enqueueSnackbar, formData.quiz_config.difficulty_id]);

  // Test quiz configuration
  const testQuizConfiguration = async () => {
    try {
      // Convert live quiz config to normal quiz config format
      const quizConfig = {
        difficulty_id: formData.quiz_config.difficulty_id,
        topic_id: '', // No specific topic for live quiz
        duration_minutes: formData.quiz_config.duration || 5,
        questions_per_song: formData.quiz_config.questions_per_song || 3,
        number_of_songs: formData.quiz_config.number_of_songs || 20
      };
      
      // Test the configuration by calling the quiz questions API
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/quiz/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quizConfig),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to test quiz configuration');
      }
      
      const data = await response.json();
      const questionsPerSong = formData.quiz_config.questions_per_song || 3;
      const totalQuestions = questionsPerSong * (formData.quiz_config.number_of_songs || 20);
      enqueueSnackbar(`✅ Configuration valid! ${data.quiz_config.total_songs} songs, ${questionsPerSong} questions per song, ${totalQuestions} total questions.`, { variant: 'success' });
      
    } catch (error) {
      console.error('Quiz configuration test error:', error);
      enqueueSnackbar(`❌ Configuration error: ${error.message}`, { variant: 'error' });
    }
  };

  // Create new live quiz session
  const createSession = async () => {
    try {
      // Validate form data
      if (!formData.quiz_name.trim()) {
        enqueueSnackbar('Quiz name is required', { variant: 'error' });
        return;
      }
      
      if (formData.quiz_config.duration < 1 || formData.quiz_config.duration > 60) {
        enqueueSnackbar('Duration must be between 1 and 60 minutes', { variant: 'error' });
        return;
      }
      
      if (formData.quiz_config.questions_per_song < 1 || formData.quiz_config.questions_per_song > 10) {
        enqueueSnackbar('Questions per song must be between 1 and 10', { variant: 'error' });
        return;
      }
      
      if (formData.quiz_config.number_of_songs < 1 || formData.quiz_config.number_of_songs > 100) {
        enqueueSnackbar('Number of songs must be between 1 and 100', { variant: 'error' });
        return;
      }

      // Prepare the session data with questions_per_song directly
      const sessionData = {
        ...formData,
        quiz_config: {
          ...formData.quiz_config,
          questions_per_song: formData.quiz_config.questions_per_song
        }
      };

      if (editingSession) {
        // Update existing session
        await updateSessionAPI(editingSession.session_id, sessionData);
        enqueueSnackbar('Live quiz session updated successfully!', { variant: 'success' });
      } else {
        // Create new session
        await createSessionAPI(sessionData);
      enqueueSnackbar('Live quiz session created successfully!', { variant: 'success' });
      }
      setCreateDialogOpen(false);
      setEditingSession(null);
      resetForm();
    } catch (err) {
      enqueueSnackbar(`Failed to ${editingSession ? 'update' : 'create'} live quiz session`, { variant: 'error' });
    }
  };

  // Activate/Deactivate session
  const toggleSessionStatus = async (sessionId, currentStatus) => {
    try {
      await toggleSessionStatusAPI(sessionId, currentStatus);
      const action = currentStatus === 'active' ? 'deactivated' : 'activated';
      enqueueSnackbar(`Session ${action} successfully!`, { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(`Failed to toggle session status`, { variant: 'error' });
    }
  };

  // Delete session
  const deleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this live quiz session?')) {
      return;
    }
    
    try {
      await deleteSessionAPI(sessionId);
      enqueueSnackbar('Session deleted successfully!', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to delete session', { variant: 'error' });
    }
  };

  // View session details
  const viewSession = (session) => {
    setSelectedSession(session);
    setViewDialogOpen(true);
  };

  // Edit session
  const editSession = (session) => {
    setEditingSession(session);
    const sessionConfig = JSON.parse(session.quiz_config);
    setFormData({
      quiz_name: session.quiz_name,
      quiz_config: {
        duration: sessionConfig.duration || 5,
        difficulty_id: sessionConfig.difficulty_id || sessionConfig.difficulty, // Handle both old and new format
        questions_per_song: sessionConfig.questions_per_song || 3,
        number_of_songs: sessionConfig.number_of_songs || 20
      },
      quiz_data: JSON.parse(session.quiz_data),
      scheduled_start_time: session.scheduled_start_time ? new Date(session.scheduled_start_time).toISOString().slice(0, 16) : '',
      scheduled_end_time: session.scheduled_end_time ? new Date(session.scheduled_end_time).toISOString().slice(0, 16) : ''
    });
    setCreateDialogOpen(true);
  };

  // Test session (play quiz as admin)
  const testSession = (session) => {
    // Navigate to quiz with admin test mode
    window.open(`/quiz?test=true&sessionId=${session.session_id}`, '_blank');
  };

  // View participants
  const viewParticipants = async (session) => {
    try {
      setSelectedSession(session);
      setLoadingParticipants(true);
      setParticipants([]); // Initialize with empty array
      setParticipantsDialogOpen(true);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${session.session_id}/participants`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();

      // console.log("data: ", data);
      
      if (data.success && Array.isArray(data.participants)) {
        // Filter out any invalid entries
        const validParticipants = data.participants.filter(p => p && typeof p === 'object');
        setParticipants(validParticipants);
      } else {
        // If no participants or invalid response, set empty array
        setParticipants([]);
      }
    } catch (error) {
      console.error('Error fetching participants:', error);
      enqueueSnackbar('Failed to fetch participants', { variant: 'error' });
      setParticipants([]); // Ensure empty array on error
    } finally {
      setLoadingParticipants(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      quiz_name: '',
      quiz_config: {
        duration: 5,
        difficulty_id: difficulties.length > 0 ? difficulties[0].difficulty_id : null,
        questions_per_song: 3,
        number_of_songs: 20
      },
      quiz_data: {
        questions: []
      },
      scheduled_start_time: '',
      scheduled_end_time: ''
    });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleString();
  };

  // Format duration
  const formatDuration = (minutes) => {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };

  // Load difficulties on mount
  useEffect(() => {
    fetchDifficulties();
  }, [fetchDifficulties]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: '#4CAF50' }} />
        <Typography variant="h6" sx={{ ml: 2, color: '#fff' }}>
          Loading live quiz sessions...
        </Typography>
      </Box>
    );
  }

  return (
    <ManagementContainer>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
            Live Quiz Management
          </Typography>
          <Typography variant="body1" sx={{ color: '#ccc' }}>
            Create and manage live quiz sessions for real-time multiplayer quizzes
          </Typography>
        </Box>
        <ActionButton
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Live Quiz
        </ActionButton>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent sx={{ textAlign: 'center' }}>
              <LiveIcon sx={{ fontSize: 40, color: '#4CAF50', mb: 1 }} />
              <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                {sessions.length}
              </Typography>
              <Typography variant="body2" sx={{ color: '#ccc' }}>
                Total Sessions
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent sx={{ textAlign: 'center' }}>
              <PlayIcon sx={{ fontSize: 40, color: '#2196F3', mb: 1 }} />
              <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                {sessions.filter(s => s.status === 'active').length}
              </Typography>
              <Typography variant="body2" sx={{ color: '#ccc' }}>
                Active Sessions
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent sx={{ textAlign: 'center' }}>
              <PeopleIcon sx={{ fontSize: 40, color: '#FF9800', mb: 1 }} />
              <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                {sessions.reduce((sum, s) => sum + (s.participant_count || 0), 0)}
              </Typography>
              <Typography variant="body2" sx={{ color: '#ccc' }}>
                Total Participants
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent sx={{ textAlign: 'center' }}>
              <TimerIcon sx={{ fontSize: 40, color: '#9C27B0', mb: 1 }} />
              <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                {sessions.length > 0 ? Math.round(sessions.reduce((sum, s) => {
                  const config = typeof s.quiz_config === 'string' ? JSON.parse(s.quiz_config) : s.quiz_config;
                  return sum + (config.duration || 5);
                }, 0) / sessions.length) : 0}m
              </Typography>
              <Typography variant="body2" sx={{ color: '#ccc' }}>
                Avg. Duration
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Sessions Table */}
      <StyledCard>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" sx={{ color: '#fff' }}>
              Live Quiz Sessions
            </Typography>
            <IconButton onClick={fetchSessions} sx={{ color: '#4CAF50' }}>
              <RefreshIcon />
            </IconButton>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {sessions.length === 0 ? (
            <Box textAlign="center" py={4}>
              <LiveIcon sx={{ fontSize: 64, color: '#666', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#ccc', mb: 1 }}>
                No live quiz sessions yet
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Create your first live quiz session to get started
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ backgroundColor: '#121212' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Quiz Name</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Participants</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Duration</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Created</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.map((session) => {
                    const config = typeof session.quiz_config === 'string' 
                      ? JSON.parse(session.quiz_config) 
                      : session.quiz_config;
                    
                    return (
                      <TableRow key={session.session_id}>
                        <TableCell sx={{ color: '#fff' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {session.quiz_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusChip 
                            label={session.status.toUpperCase()} 
                            status={session.status}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#fff' }}>
                          {session.participant_count || 0}
                        </TableCell>
                        <TableCell sx={{ color: '#fff' }}>
                          {formatDuration(config.duration || 5)}
                        </TableCell>
                        <TableCell sx={{ color: '#fff' }}>
                          {formatDate(session.created_at)}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Tooltip title={session.status === 'active' ? 'Deactivate' : 'Activate'}>
                              <IconButton
                                size="small"
                                onClick={() => toggleSessionStatus(session.session_id, session.status)}
                                sx={{ 
                                  color: session.status === 'active' ? '#f44336' : '#4CAF50' 
                                }}
                              >
                                {session.status === 'active' ? <StopIcon /> : <PlayIcon />}
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                sx={{ color: '#2196F3' }}
                                onClick={() => viewSession(session)}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                sx={{ color: '#FF9800' }}
                                onClick={() => editSession(session)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="View Participants">
                              <IconButton
                                size="small"
                                sx={{ color: '#00BCD4' }}
                                onClick={() => viewParticipants(session)}
                              >
                                <PeopleIcon />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Test Quiz">
                              <IconButton
                                size="small"
                                sx={{ color: '#9C27B0' }}
                                onClick={() => testSession(session)}
                              >
                                <PlayIcon />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                sx={{ color: '#f44336' }}
                                onClick={() => deleteSession(session.session_id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </StyledCard>

      {/* Create Session Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1A1A1A',
            color: '#fff',
            border: '1px solid #333'
          }
        }}
      >
        <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid #333' }}>
          {editingSession ? 'Edit Live Quiz Session' : 'Create New Live Quiz Session'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Quiz Name"
                value={formData.quiz_name}
                onChange={(e) => setFormData({...formData, quiz_name: e.target.value})}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#4CAF50' },
                    '&.Mui-focused fieldset': { borderColor: '#4CAF50' },
                  },
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#4CAF50' },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Duration (minutes)"
                type="number"
                value={formData.quiz_config.duration}
                onChange={(e) => setFormData({
                  ...formData, 
                  quiz_config: {...formData.quiz_config, duration: parseInt(e.target.value)}
                })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#4CAF50' },
                    '&.Mui-focused fieldset': { borderColor: '#4CAF50' },
                  },
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#4CAF50' },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#ccc' }}>Difficulty</InputLabel>
                <Select
                  value={formData.quiz_config.difficulty_id || ''}
                  onChange={(e) => setFormData({
                    ...formData, 
                    quiz_config: {...formData.quiz_config, difficulty_id: e.target.value}
                  })}
                  disabled={loadingDifficulties}
                  sx={{
                    color: '#fff',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4CAF50' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4CAF50' },
                  }}
                >
                  {difficulties.map((difficulty) => (
                    <MenuItem key={difficulty.difficulty_id} value={difficulty.difficulty_id}>
                      {difficulty.level_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Questions Per Song Ratio Buttons */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ color: '#fff', mb: 2 }}>
                Questions Per Song
              </Typography>
              <Box display="flex" gap={2}>
                {[5, 10, 15].map((count) => (
                  <Button
                    key={count}
                    variant={formData.quiz_config.questions_per_song === count ? "contained" : "outlined"}
                    onClick={() => setFormData({
                      ...formData, 
                      quiz_config: {...formData.quiz_config, questions_per_song: count}
                    })}
                    sx={{
                      backgroundColor: formData.quiz_config.questions_per_song === count ? '#4CAF50' : 'transparent',
                      color: formData.quiz_config.questions_per_song === count ? '#fff' : '#4CAF50',
                      borderColor: '#4CAF50',
                      '&:hover': {
                        backgroundColor: formData.quiz_config.questions_per_song === count ? '#45a049' : 'rgba(76, 175, 80, 0.1)',
                        borderColor: '#4CAF50',
                      },
                      minWidth: '60px',
                    }}
                  >
                    {count}
                  </Button>
                ))}
              </Box>
              
              {/* Display calculated total questions */}
              <Box mt={2}>
                {(() => {
                  const questionsPerSong = formData.quiz_config.questions_per_song || 3;
                  const numberOfSongs = formData.quiz_config.number_of_songs || 20;
                  const totalQuestions = questionsPerSong * numberOfSongs;
                  const isValid = questionsPerSong >= 1 && questionsPerSong <= 10;
                  
                  return (
                    <>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: isValid ? '#4CAF50' : '#f44336', 
                          fontWeight: 600 
                        }}
                      >
                        Total questions: {totalQuestions}
                      </Typography>
                    </>
                  );
                })()}
              </Box>
            </Grid>
            
            {/* Number of Songs */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Number of Songs"
                type="number"
                value={formData.quiz_config.number_of_songs}
                onChange={(e) => setFormData({
                  ...formData, 
                  quiz_config: {...formData.quiz_config, number_of_songs: parseInt(e.target.value)}
                })}
                helperText="Songs will be selected randomly from the database"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#4CAF50' },
                    '&.Mui-focused fieldset': { borderColor: '#4CAF50' },
                  },
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#4CAF50' },
                  '& .MuiFormHelperText-root': { color: '#999' },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="datetime-local"
                value={formData.scheduled_start_time}
                onChange={(e) => setFormData({...formData, scheduled_start_time: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#4CAF50' },
                    '&.Mui-focused fieldset': { borderColor: '#4CAF50' },
                  },
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#4CAF50' },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Time"
                type="datetime-local"
                value={formData.scheduled_end_time}
                onChange={(e) => setFormData({...formData, scheduled_end_time: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#4CAF50' },
                    '&.Mui-focused fieldset': { borderColor: '#4CAF50' },
                  },
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#4CAF50' },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #333', p: 2 }}>
          <Button 
            onClick={() => setCreateDialogOpen(false)}
            sx={{ color: '#ccc' }}
          >
            Cancel
          </Button>
          <Button
            onClick={testQuizConfiguration}
            sx={{
              color: '#FF9800',
              border: '1px solid #FF9800',
              mr: 1,
              '&:hover': {
                borderColor: '#F57C00',
                backgroundColor: 'rgba(255, 152, 0, 0.1)'
              }
            }}
          >
            Test Configuration
          </Button>
          <ActionButton onClick={createSession}>
            {editingSession ? 'Update Session' : 'Create Session'}
          </ActionButton>
        </DialogActions>
      </Dialog>
      
      {/* View Session Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1A1A1A',
            border: '1px solid #333',
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid #333' }}>
          Session Details: {selectedSession?.quiz_name}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedSession && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1 }}>
                  Quiz Name
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
                  {selectedSession.quiz_name}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1 }}>
                  Status
                </Typography>
                <StatusChip 
                  status={selectedSession.status} 
                  label={selectedSession.status.charAt(0).toUpperCase() + selectedSession.status.slice(1)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1 }}>
                  Duration
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
                  {JSON.parse(selectedSession.quiz_config).duration} minutes
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1 }}>
                  Difficulty
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
                  {(() => {
                    const sessionConfig = JSON.parse(selectedSession.quiz_config);
                    const difficultyId = sessionConfig.difficulty_id || sessionConfig.difficulty;
                    const difficulty = difficulties.find(d => d.difficulty_id === difficultyId);
                    return difficulty ? difficulty.level_name : (sessionConfig.difficulty || 'Unknown');
                  })()}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1 }}>
                  Number of Questions
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
                  {JSON.parse(selectedSession.quiz_config).question_count}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1 }}>
                  Number of Songs
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
                  {JSON.parse(selectedSession.quiz_config).number_of_songs || 'Not set'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1 }}>
                  Scheduled Start Time
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
                  {formatDate(selectedSession.scheduled_start_time)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1 }}>
                  Scheduled End Time
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
                  {formatDate(selectedSession.scheduled_end_time)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1 }}>
                  Actual Start Time
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
                  {formatDate(selectedSession.actual_start_time)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1 }}>
                  Participant Count
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
                  {selectedSession.participant_count || 0}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1 }}>
                  Created At
                </Typography>
                <Typography variant="body1" sx={{ color: '#fff', mb: 2 }}>
                  {formatDate(selectedSession.created_at)}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #333', p: 2 }}>
          <Button 
            onClick={() => setViewDialogOpen(false)}
            sx={{ color: '#ccc' }}
          >
            Close
          </Button>
          <ActionButton onClick={() => {
            setViewDialogOpen(false);
            testSession(selectedSession);
          }}>
            Test This Quiz
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* Participants Dialog */}
      <Dialog 
        open={participantsDialogOpen} 
        onClose={() => setParticipantsDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1A1A1A',
            color: '#fff',
            border: '1px solid #333'
          }
        }}
      >
        <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid #333', display: 'flex', alignItems: 'center' }}>
          <PeopleIcon sx={{ mr: 1, color: '#00BCD4' }} />
          Participants - {selectedSession?.quiz_name}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {loadingParticipants ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress sx={{ color: '#4CAF50' }} />
              <Typography variant="body1" sx={{ color: '#fff', ml: 2 }}>
                Loading participants...
              </Typography>
            </Box>
          ) : (!participants || participants.length === 0) ? (
            <Box textAlign="center" py={4}>
              <PeopleIcon sx={{ fontSize: 64, color: '#666', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#ccc', mb: 1 }}>
                No participants yet
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Participants will appear here when they join the live quiz
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ backgroundColor: '#121212' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Score</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Accuracy</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Questions</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Time</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Position</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Joined At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participants && participants.length > 0 ? participants.map((participant) => {
                    // Add safety checks for participant data
                    if (!participant) return null;
                    
                    return (
                      <TableRow key={participant.participant_id || Math.random()} sx={{ '&:hover': { backgroundColor: '#2A2A2A' } }}>
                      <TableCell sx={{ color: '#fff' }}>
                        {participant.user_id ? 
                          (() => {
                            const firstName = participant.fname || '';
                            const lastName = participant.lname || '';
                            const fullName = `${firstName} ${lastName}`.trim();
                            return fullName || 'Registered User';
                          })() :
                          participant.guest_name || 'Guest User'
                        }
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={participant.user_id ? 'Registered' : 'Guest'}
                          size="small"
                          sx={{
                            backgroundColor: participant.user_id ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 152, 0, 0.2)',
                            color: participant.user_id ? '#4CAF50' : '#FF9800',
                            border: `1px solid ${participant.user_id ? '#4CAF50' : '#FF9800'}`,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 600 }}>
                        {(() => {
                          const score = participant.current_score || 0;
                          let color = '#fff';
                          if (score >= 80) color = '#4CAF50'; // Green for high scores
                          else if (score >= 60) color = '#FF9800'; // Orange for medium scores
                          else if (score > 0) color = '#f44336'; // Red for low scores
                          
                          return (
                            <span style={{ color }}>
                              {score}
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ color: '#fff' }}>
                        {(() => {
                          const percentage = participant.percentage;
                          if (!percentage) return '-';
                          
                          let color = '#fff';
                          if (percentage >= 80) color = '#4CAF50';
                          else if (percentage >= 60) color = '#FF9800';
                          else if (percentage > 0) color = '#f44336';
                          
                          return (
                            <span style={{ color }}>
                              {percentage}%
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ color: '#fff' }}>
                        {participant.correct_answers && participant.total_questions ? 
                          `${participant.correct_answers}/${participant.total_questions}` : '-'}
                      </TableCell>
                      <TableCell sx={{ color: '#fff' }}>
                        {participant.time_taken_seconds ? 
                          `${Math.floor(participant.time_taken_seconds / 60)}:${(participant.time_taken_seconds % 60).toString().padStart(2, '0')}` : '-'}
                      </TableCell>
                      <TableCell sx={{ color: '#fff' }}>
                        {participant.current_position ? `#${participant.current_position}` : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={participant.status || 'Active'}
                          size="small"
                          sx={{
                            backgroundColor: participant.status === 'completed' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(33, 150, 243, 0.2)',
                            color: participant.status === 'completed' ? '#4CAF50' : '#2196F3',
                            border: `1px solid ${participant.status === 'completed' ? '#4CAF50' : '#2196F3'}`,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#ccc' }}>
                        {participant.joined_at ? new Date(participant.joined_at).toLocaleString() : '-'}
                      </TableCell>
                    </TableRow>
                    );
                  }) : null}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #333', p: 2 }}>
          <Button 
            onClick={() => setParticipantsDialogOpen(false)}
            sx={{ color: '#ccc' }}
          >
            Close
          </Button>
          <ActionButton onClick={() => {
            setParticipantsDialogOpen(false);
            if (selectedSession) {
              viewParticipants(selectedSession);
            }
          }}>
            Refresh
          </ActionButton>
        </DialogActions>
      </Dialog>
    </ManagementContainer>
  );
};

export default LiveQuizManagement;
